import { forwardRef } from 'react';

import styles from './RadioCardGroup.module.css';
import { RadioGroup, RadioGroupProps } from './RadioGroup';

export type RadioCardGroupOrientation = 'horizontal' | 'vertical';

export type RadioCardGroupProps = RadioGroupProps & {
  /**
   * Direction the cards flow in. `horizontal` (the default) splits the row
   * evenly between the cards, replacing the `Grid.Col span={12 / n}` trick
   * consumers used to reach for. `vertical` stacks them full-width.
   */
  orientation?: RadioCardGroupOrientation;
  /**
   * Space between cards. Any CSS length; prefer a spacing token.
   * Default: `var(--space-16)`.
   */
  gap?: string;
};

/**
 * `RadioGroup` plus the card layout every consumer was hand-rolling with a
 * `Stack` or a `Grid`. Label, description, required asterisk, error and
 * read-only behaviour all come from `RadioGroup` unchanged — this only owns
 * the arrangement of the `RadioCard` children.
 */
export const RadioCardGroup = forwardRef<HTMLDivElement, RadioCardGroupProps>(
  ({ orientation = 'horizontal', gap, children, ...props }, ref) => {
    return (
      <RadioGroup ref={ref} {...props}>
        <div
          className={styles.layout}
          data-orientation={orientation}
          style={
            gap
              ? ({ '--radio-card-group-gap': gap } as React.CSSProperties)
              : undefined
          }
        >
          {children}
        </div>
      </RadioGroup>
    );
  }
);

RadioCardGroup.displayName = 'RadioCardGroup';
